import { BookOpen, MoreVertical, Pencil, Tags } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ArticleDetail as Detail } from '@shared/api';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeaderRow,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  Heading,
  PageBackLink,
  Skeleton,
  StatusBadge,
  Text,
} from '@shared/ui';
import { useArticleDeletion } from '../model/use-article-deletion';
import { ArticleDeletionFeedback } from './ArticleDeletionFeedback';
import { articleStatusLabel, articleTopicLabel } from '../model/presentation';
import { ArticleEditor } from './ArticleEditor';
import { ArticleError } from './ArticleError';
import type { ArticleTopicsState } from './ArticleList';

export function ArticleDetail({
  scope,
  id,
  canUpdate,
  canDelete,
  topics,
  onBack,
  startInEditMode = false,
  onEditEntryConsumed,
}: {
  scope: string;
  id: string;
  canUpdate: boolean;
  canDelete: boolean;
  topics: ArticleTopicsState;
  onBack: () => void;
  startInEditMode?: boolean;
  onEditEntryConsumed?: () => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState<Detail>();
  const deletion = useArticleDeletion({ scope, id, canDelete });
  const {
    detail,
    deleted,
    deletionState,
    unresolvedDelete,
    pending,
    denied,
    refresh,
    beginDelete,
  } = deletion;
  const editEntryConsumed = useRef(false);
  useEffect(() => {
    if (deleted) onBack();
  }, [deleted, onBack]);
  useEffect(() => {
    if (
      !startInEditMode ||
      editEntryConsumed.current ||
      !canUpdate ||
      !detail.data ||
      detail.error ||
      detail.isFetching ||
      detail.data.data.deletion
    )
      return;
    editEntryConsumed.current = true;
    setEditing(detail.data);
    onEditEntryConsumed?.();
  }, [
    canUpdate,
    detail.data,
    detail.error,
    detail.isFetching,
    onEditEntryConsumed,
    startInEditMode,
  ]);
  const article = editing?.data ?? detail.data?.data;
  return (
    <section className="flex min-w-0 flex-col gap-4" aria-label={t('articles.detail')}>
      <PageBackLink onClick={onBack}>{t('articles.back')}</PageBackLink>
      {deleted ? null : denied && !editing ? (
        <ArticleError error={detail.error} onRetry={refresh} />
      ) : (
        <>
          {detail.isPending && !editing ? (
            <div role="status" aria-label={t('common.loading')}>
              <Skeleton className="h-64 w-full" />
            </div>
          ) : null}
          {detail.error && !article ? (
            <ArticleError error={detail.error} onRetry={refresh} />
          ) : article ? (
            <>
              <header className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-stretch">
                <div className="flex h-profile-header-media w-profile-header-media shrink-0 items-center justify-center border border-border bg-muted">
                  <BookOpen className="size-8 text-muted-foreground" aria-hidden="true" />
                </div>
                <div className="min-w-0 flex-1 space-y-3">
                  <CardHeaderRow
                    controls={
                      canDelete && !editing && !deletionState && !unresolvedDelete ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={t('common.actions')}
                              disabled={pending}
                            >
                              <MoreVertical aria-hidden="true" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              variant="destructive"
                              onSelect={() => {
                                void beginDelete();
                              }}
                            >
                              {t('articles.delete')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : undefined
                    }
                  >
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                      <Heading as="h1" size="lg" className="min-w-0 break-words">
                        {article.title ?? t('crm.missingValue')}
                      </Heading>
                      {article.status?.trim() ? (
                        <StatusBadge tone={article.status === 'Approved' ? 'positive' : 'neutral'}>
                          {articleStatusLabel(article.status, t)}
                        </StatusBadge>
                      ) : null}
                    </div>
                  </CardHeaderRow>
                  {article.topic ? (
                    <div className="flex flex-wrap gap-2">
                      <Badge appearance="outline" icon={<Tags size={16} />} size="md" wrap="wrap">
                        {t('articles.fields.topic')}: {articleTopicLabel(article.topic, t)}
                      </Badge>
                    </div>
                  ) : null}
                  {detail.error && !editing ? (
                    <ArticleError error={detail.error} onRetry={refresh} />
                  ) : null}
                  <ArticleDeletionFeedback deletion={deletion} canDelete={canDelete} />
                </div>
              </header>
              <Card>
                {editing ? (
                  <ArticleEditor
                    scope={scope}
                    initial={editing}
                    topics={topics}
                    embedded
                    canSave={canUpdate && !denied}
                    onCancel={() => {
                      setEditing(undefined);
                    }}
                    onSaved={() => {
                      setEditing(undefined);
                    }}
                  />
                ) : (
                  <>
                    <CardContent className="min-w-0 space-y-3 p-4">
                      <CardHeaderRow>
                        <Heading as="h2" size="md">
                          {t('articles.information')}
                        </Heading>
                      </CardHeaderRow>
                      <section className="space-y-2" aria-labelledby="article-content-heading">
                        <Text id="article-content-heading" size="sm" color="fieldLabel">
                          {t('articles.fields.content')}
                        </Text>
                        <Text className="whitespace-pre-wrap break-words">
                          {article.content ?? t('crm.missingValue')}
                        </Text>
                      </section>
                    </CardContent>
                    {canUpdate && !deletionState && !unresolvedDelete ? (
                      <CardFooter className="flex-wrap justify-end gap-2 p-4 pt-0">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={pending}
                          onClick={() => {
                            if (detail.data) setEditing(detail.data);
                          }}
                        >
                          <Pencil aria-hidden="true" />
                          {t('articles.edit')}
                        </Button>
                      </CardFooter>
                    ) : null}
                  </>
                )}
              </Card>
            </>
          ) : null}
        </>
      )}
    </section>
  );
}
